import { useState } from 'react';
import { useLanguage } from '@/i18n/LanguageContext';
import { nextStep, type NextStepInput, type StepAction } from './receivingModel';
import type { TranslationKey } from '@/i18n/dictionaries/en';

const ACTION_KEYS: Record<StepAction, TranslationKey> = {
  START: 'rcv.act.START',
  ARRIVE: 'rcv.act.ARRIVE',
  FINISH: 'rcv.act.FINISH',
  CLOSE: 'rcv.act.CLOSE',
};

/**
 * The block an operator reads first: where this document stands, what to press next here, how the
 * same step is done on the handheld (with the document number to look for - copyable), and what is
 * locked and why. The decision logic is `nextStep` (pure, unit-tested).
 */
export function NextStepCard({
  number, receivingCodes, input, busy, onAction,
}: {
  readonly number: string;
  readonly receivingCodes: readonly string[];
  readonly input: NextStepInput;
  readonly busy: boolean;
  readonly onAction: (a: StepAction) => void;
}): JSX.Element {
  const { t } = useLanguage();
  const [copied, setCopied] = useState(false);
  const step = nextStep(input);

  async function copy(): Promise<void> {
    try {
      await navigator.clipboard.writeText(number);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // the clipboard may be unavailable (insecure context); the number is on screen to read out
    }
  }

  return (
    <section className="card next-step" aria-labelledby="next-step-title" data-testid="next-step">
      <h2 id="next-step-title" className="card-title">{t('rcv.nextTitle')}</h2>
      <p className="next-step-text">{t(step.textKey)}</p>

      {step.tsdKey && (
        <div className="tsd-handoff" data-testid="tsd-handoff">
          <div>
            {t('rcv.tsdPath')} <strong className="doc-number">{number}</strong>
            <button type="button" className="link-button" onClick={() => void copy()} aria-label={t('rcv.copy')}>
              {copied ? t('rcv.copied') : t('rcv.copy')}
            </button>
          </div>
          <div className="meta">
            {t(step.tsdKey, { locations: receivingCodes.length > 0 ? receivingCodes.join(', ') : '—' })}
          </div>
        </div>
      )}

      {step.tsdKey && (
        <p className="meta" data-testid="flow-note">
          {t('rcv.flowNote')}
        </p>
      )}

      {step.warn && <div className="notice" role="status">{t(step.warn.key, step.warn.params)}</div>}

      {(step.primary || step.secondary) && (
        <div className="action-row">
          {step.primary && (
            <button type="button" className="primary" disabled={busy || !step.primaryEnabled} onClick={() => onAction(step.primary!)}>
              {t(ACTION_KEYS[step.primary])}
            </button>
          )}
          {step.secondary && (
            <button type="button" disabled={busy} onClick={() => onAction(step.secondary!)}>
              {t(ACTION_KEYS[step.secondary])}
            </button>
          )}
        </div>
      )}

      {step.blocked && (
        <p className="meta blocked-reason" role="note" data-testid="blocked-reason">
          {t(step.blocked.key, step.blocked.params)}
        </p>
      )}
    </section>
  );
}
